import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ShieldCheck, Upload, FileCheck, AlertCircle, Loader2, 
  CreditCard, Car, User, CheckCircle, X, Eye, Clock
} from 'lucide-react';

const DOCUMENT_TYPES = [
  { value: 'ghana_card', label: 'Ghana Card', description: 'National Identity Card' },
  { value: 'drivers_license', label: "Driver's License", description: 'Ghana Driver License' },
  { value: 'passport', label: 'Passport', description: 'International Passport' },
];

const STATUS_CONFIG = {
  verified: { label: 'Verified', color: 'bg-trust-green/20 text-trust-green', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-400', icon: X },
  pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
};

export default function KYCVerification() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedType, setSelectedType] = useState('ghana_card');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchKYCData();
  }, [user, navigate]);

  const fetchKYCData = async () => {
    try {
      const [statusRes, docsRes] = await Promise.all([
        API.get('/kyc/status'),
        API.get('/kyc/documents')
      ]);
      setKycStatus(statusRes.data);
      setDocuments(docsRes.data || []);
    } catch (err) {
      console.error('Failed to fetch KYC data:', err);
    }
    setLoading(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPG, PNG, or WEBP images allowed');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10MB)');
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      toast.error('Please select a document image');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('document_type', selectedType);
    formData.append('document_image', selectedFile);

    try {
      const res = await API.post('/kyc/verify', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.status === 'verified') {
        toast.success('Document verified successfully! Your account is now KYC verified.');
        setUser(prev => ({ ...prev, kyc_verified: true }));
      } else {
        toast.error('Document verification failed. Please try again with a clearer image.');
      }

      // Refresh data
      await fetchKYCData();
      setSelectedFile(null);
      setPreviewUrl(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    }
    setUploading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <main className="min-h-screen py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Identity Verification</h1>
          <p className="text-muted-foreground">
            Verify your identity to build trust and unlock premium features
          </p>
        </div>

        {/* Current Status */}
        <div className="bg-card border border-border/50 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                kycStatus?.is_verified ? 'bg-trust-green/20' : 'bg-muted'
              }`}>
                <ShieldCheck className={`w-7 h-7 ${
                  kycStatus?.is_verified ? 'text-trust-green' : 'text-muted-foreground'
                }`} />
              </div>
              <div>
                <h2 className="text-xl font-semibold">
                  {kycStatus?.is_verified ? 'Identity Verified' : 'Not Verified'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {kycStatus?.is_verified 
                    ? `Verified on ${new Date(kycStatus.verified_at).toLocaleDateString('en-GH')}`
                    : 'Complete verification to earn a trust badge'
                  }
                </p>
              </div>
            </div>
            {kycStatus?.is_verified && (
              <Badge className="bg-trust-green/20 text-trust-green border-none gap-1">
                <CheckCircle className="w-3.5 h-3.5" /> Verified
              </Badge>
            )}
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-border/50">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-gold" />
              </div>
              <div>
                <h3 className="font-medium text-sm">Trust Badge</h3>
                <p className="text-xs text-muted-foreground">Display verified badge on your listings</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-5 h-5 text-gold" />
              </div>
              <div>
                <h3 className="font-medium text-sm">Higher Limits</h3>
                <p className="text-xs text-muted-foreground">Increased escrow transaction limits</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0">
                <Car className="w-5 h-5 text-gold" />
              </div>
              <div>
                <h3 className="font-medium text-sm">Premium Features</h3>
                <p className="text-xs text-muted-foreground">Access to featured listings</p>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Section */}
        {!kycStatus?.is_verified && (
          <div className="bg-card border border-border/50 rounded-xl p-6 mb-8">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5" /> Upload Document
            </h3>

            <div className="space-y-4">
              {/* Document Type */}
              <div>
                <Label className="text-sm mb-2 block">Document Type</Label>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger className="w-full" data-testid="kyc-document-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map(doc => (
                      <SelectItem key={doc.value} value={doc.value}>
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4" />
                          <span>{doc.label}</span>
                          <span className="text-xs text-muted-foreground">({doc.description})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* File Upload */}
              <div>
                <Label className="text-sm mb-2 block">Document Image</Label>
                <div className="border-2 border-dashed border-border/50 rounded-xl p-6 text-center hover:border-gold/50 transition-colors">
                  {previewUrl ? (
                    <div className="relative">
                      <img 
                        src={previewUrl} 
                        alt="Preview" 
                        className="max-h-64 mx-auto rounded-lg"
                      />
                      <button
                        onClick={() => { setSelectedFile(null); setPreviewUrl(null); }}
                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-500/80 flex items-center justify-center text-white hover:bg-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleFileSelect}
                        className="hidden"
                        data-testid="kyc-file-input"
                      />
                      <div className="py-8">
                        <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                        <p className="text-sm font-medium">Click to upload or drag and drop</p>
                        <p className="text-xs text-muted-foreground mt-1">JPG, PNG or WEBP (max 10MB)</p>
                      </div>
                    </label>
                  )}
                </div>
              </div>

              {/* Guidelines */}
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-gold" /> Guidelines
                </h4>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Ensure the entire document is visible and not cropped</li>
                  <li>• Image should be clear and not blurry</li>
                  <li>• All text should be readable</li>
                  <li>• Document must be valid and not expired</li>
                  <li>• No filters or edits to the image</li>
                </ul>
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleSubmit}
                disabled={!selectedFile || uploading}
                className="w-full bg-gold text-black hover:brightness-110 h-12 font-semibold"
                data-testid="kyc-submit-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4 mr-2" /> Verify Document
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Submission History */}
        {documents.length > 0 && (
          <div className="bg-card border border-border/50 rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileCheck className="w-5 h-5" /> Submission History
            </h3>

            <div className="space-y-3">
              {documents.map(doc => {
                const statusConfig = STATUS_CONFIG[doc.status] || STATUS_CONFIG.pending;
                const StatusIcon = statusConfig.icon;

                return (
                  <div 
                    key={doc.kyc_id} 
                    className="flex items-center justify-between p-4 bg-muted/30 rounded-lg"
                    data-testid={`kyc-doc-${doc.kyc_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {DOCUMENT_TYPES.find(d => d.value === doc.document_type)?.label || doc.document_type}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(doc.submitted_at).toLocaleDateString('en-GH', {
                            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={`${statusConfig.color} border-none gap-1`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.label}
                      </Badge>
                      {doc.confidence_score !== undefined && (
                        <span className="text-xs text-muted-foreground">
                          {doc.confidence_score}% confidence
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
